// Seedable PRNG + closed-form Calendar.
//
// Calendar is computed on-demand from (tzOffset, oooBlocks, windowHrs) — we
// don't precompute the available-interval array, which was producing tens of
// thousands of small Array objects per Monte Carlo run and blowing up the
// browser's young-gen GC pressure.

export type PRNG = () => number;

export function makePRNG(seed: number): PRNG {
  let s = (seed | 0) || 1;
  return function (): number {
    s = (s + 0x6D2B79F5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function gauss(rand: PRNG): number {
  const u = 1 - rand();
  const v = rand();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

export function lognormal(rand: PRNG, meanHrs: number, sigma = 0.6): number {
  const mu = Math.log(Math.max(1e-3, meanHrs)) - 0.5 * sigma * sigma;
  return Math.exp(mu + sigma * gauss(rand));
}

export type TzAbbr = "HST" | "AKST" | "PT" | "MT" | "CT" | "ET";

// North America span — ET anchor through Hawaii/Alaska. The wider tail
// gives the "working-hours overlap" knob enough swing to actually shift
// medians; the continental US-only PT-to-ET spread is only 3 hours.
export const TZ_OFFSET_FROM_ET: Record<TzAbbr, number> = {
  HST: -5, AKST: -4, PT: -3, MT: -2, CT: -1, ET: 0,
};

/** Inclusive-exclusive pair of absolute hours when the agent is OOO. */
export type OooBlock = [number, number];

// 9-5 in local time, projected to baseline ET hours.
// For PT (tzOffset=-3) the person works 12-20 ET.
function dayWindowsForTz(tzOffsetHrs: number): { start: number; end: number } {
  return { start: 9 - tzOffsetHrs, end: 17 - tzOffsetHrs };
}

export interface CalendarOpts {
  windowHrs: number;
  tzOffsetHrs: number;
  oooBlocks: OooBlock[];
}

/**
 * Closed-form Calendar.
 *
 * State: a few numbers + a small sorted oooBlocks array (typically 0–5 pairs).
 * No precomputed interval lists. `nextAvailable` and `advanceWorkingHours`
 * walk the day grid arithmetically, jumping over weekends and OOO blocks.
 */
export class Calendar {
  windowHrs: number;
  dayStart: number;
  dayEnd: number;
  dayLen: number;
  ooo: OooBlock[];

  constructor({ windowHrs, tzOffsetHrs, oooBlocks }: CalendarOpts) {
    this.windowHrs = windowHrs;
    const win = dayWindowsForTz(tzOffsetHrs);
    this.dayStart = win.start;
    this.dayEnd = win.end;
    this.dayLen = win.end - win.start; // hours of work in one day (8)
    this.ooo = oooBlocks; // sorted [[s,e], ...]
  }

  // Earliest hour ≥ t when the person is at their desk.
  nextAvailable(t: number): number {
    let cur = Math.max(0, t);
    // Safety cap — at worst we walk every day in the window, ~30 iterations.
    for (let iter = 0; iter < 200; iter++) {
      if (cur >= this.windowHrs) return Infinity;
      const dayIdx = Math.floor(cur / 24);
      const dayInWeek = dayIdx % 7;
      const hourInDay = cur - dayIdx * 24;

      // Weekend → jump to start of next Monday's workday.
      if (dayInWeek >= 5) {
        const daysToMonday = 7 - dayInWeek;
        cur = (dayIdx + daysToMonday) * 24 + this.dayStart;
        continue;
      }
      // Before the workday → jump forward.
      if (hourInDay < this.dayStart) {
        cur = dayIdx * 24 + this.dayStart;
        continue;
      }
      // After the workday → jump to next day's start.
      if (hourInDay >= this.dayEnd) {
        cur = (dayIdx + 1) * 24 + this.dayStart;
        continue;
      }
      // Inside an OOO block? Jump to its end.
      const block = this._oooContaining(cur);
      if (block) {
        cur = block[1];
        continue;
      }
      return cur;
    }
    return Infinity;
  }

  // Walk `workHrs` of *working* time forward from `start`, skipping non-
  // working time. Returns the absolute hour when work finishes.
  advanceWorkingHours(start: number, workHrs: number): number {
    if (workHrs <= 0) return start;
    let cur = this.nextAvailable(start);
    if (!isFinite(cur)) return Infinity;
    let remaining = workHrs;
    for (let iter = 0; iter < 1000; iter++) {
      if (cur >= this.windowHrs) return Infinity;
      const dayIdx = Math.floor(cur / 24);
      const dayEndAbs = dayIdx * 24 + this.dayEnd;
      // How many hours can we fit before either (a) the workday ends or
      // (b) we hit an OOO block?
      let slotEnd = dayEndAbs;
      for (const [s] of this.ooo) {
        if (s > cur && s < slotEnd) {
          slotEnd = s;
        }
      }
      const slotLen = slotEnd - cur;
      if (slotLen >= remaining) return cur + remaining;
      remaining -= slotLen;
      cur = this.nextAvailable(slotEnd);
      if (!isFinite(cur)) return Infinity;
    }
    return Infinity;
  }

  _oooContaining(t: number): OooBlock | null {
    const ooo = this.ooo;
    for (let i = 0; i < ooo.length; i++) {
      const block = ooo[i];
      if (t >= block[0] && t < block[1]) return block;
      if (block[0] > t) break; // sorted — no later block can match
    }
    return null;
  }
}
